import React, { useMemo, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import useClickOutside from '../../hooks/useClickOutside';
import useEscapeKey from '../../hooks/useEscapeKey';
import RippleButton from '../RippleButton';

export default function CategoryMultiSelect({ options, selected, onChange }) {
  const [open, setOpen] = React.useState(false);
  const rootRef = useRef(null);

  useClickOutside(rootRef, () => setOpen(false));
  useEscapeKey(open, () => setOpen(false));

  const selectedSet = useMemo(() => new Set(selected || []), [selected]);
  const selectedLabel = !selected || selected.length === 0 ? 'All categories' : `${selected.length} selected`;

  function toggleCategory(cat) {
    const next = new Set(selectedSet);
    if (next.has(cat)) next.delete(cat);
    else next.add(cat);
    onChange(Array.from(next));
  }

  return (
    <div className="dropdown" ref={rootRef}>
      <RippleButton className="dropdown__trigger" type="button" onClick={() => setOpen((v) => !v)}>
        <span className="dropdown__trigger-label">{selectedLabel}</span>
        <span className={`dropdown__caret ${open ? 'dropdown__caret--open' : ''}`}>▾</span>
      </RippleButton>

      <AnimatePresence>
        {open && (
          <motion.div
            className="dropdown__panel"
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.99 }}
            transition={{ duration: 0.16 }}
            role="menu"
            aria-label="Category filter"
          >
            <div className="dropdown__panel-header">
              <div className="dropdown__panel-title">Filter categories</div>
              <button
                className="dropdown__link"
                type="button"
                onClick={() => onChange([])}
                disabled={!selected || selected.length === 0}
              >
                Clear
              </button>
            </div>

            <div className="dropdown__list">
              {!options || options.length === 0 ? (
                <div className="dropdown__empty">No categories yet</div>
              ) : (
                options.map((opt) => (
                  <label key={opt} className="dropdown__item">
                    <input
                      type="checkbox"
                      checked={selectedSet.has(opt)}
                      onChange={() => toggleCategory(opt)}
                    />
                    <span className="dropdown__item-label">{opt}</span>
                  </label>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

